import { PageError } from '@ctw/shared/components/errors/page-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ZusAggregatedProfile } from '@ctw/shared/content/zus-aggregated-profile';
import { CTWProvider } from '@ctw/shared/context/ctw-context';
import { PatientProvider } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getApiServiceBaseUrl, getEnvFromCurrentHostname } from '@ctw/shared/utils/environments';
import { useEffect, useState } from 'react';
import { loadSdk } from 'vim-os-js-browser';
import type { EHR, SDK } from 'vim-os-js-browser/types';
import { VimPatientInjector } from '../vim-context/vim-patient-injector';

const VimPatientIdentifierSystemUrl = 'https://api.getvim.com/v1/patient-id';

export const VimV1 = () => {
  const [vimOS, setVimOS] = useState<SDK | undefined>(undefined);
  const [vimInitializationError, setVimInitializationError] = useState<Error | null>(null);
  const [idToken, setIdToken] = useState<string | undefined>(undefined);
  const [zusToken, setZusToken] = useState<string | undefined>(undefined);
  const [zusBuilderId, setZusBuilderId] = useState<string | undefined>(undefined);
  const [patient, setPatient] = useState<EHR.Patient | undefined>(undefined);
  const telemetry = useTelemetry();

  const ehrHooksUrl = getApiServiceBaseUrl({
    service: 'ehr-hooks',
    env: getEnvFromCurrentHostname({ fallbackEnv: 'sandbox' }),
  });

  useEffect(() => {
    (async () => {
      let loadedVimOS: SDK;
      try {
        loadedVimOS = await loadSdk();
      } catch (error) {
        const msg = 'Failed to load Vim SDK';
        telemetry.trackError({
          message: msg,
          error: error instanceof Error ? error : new Error(msg),
          context: {
            error,
          },
        });
        setVimInitializationError(new Error(msg));
        return;
      }
      // Turns on the ZAP button in the vim panel
      loadedVimOS.hub.setActivationStatus('ENABLED');
      loadedVimOS.ehr.subscribe('patient', (subscribedPatient) => {
        if (subscribedPatient) {
          setPatient(subscribedPatient);
        }
      });
      setVimOS(loadedVimOS);
    })();
  }, [telemetry.trackError]);

  useEffect(() => {
    if (vimOS) {
      (async () => {
        try {
          const token = await vimOS.sessionContext.getIdToken();
          setIdToken(token.idToken);
        } catch (error) {
          const msg = 'Failed to get ID token from Vim SDK';
          telemetry.trackError({
            message: msg,
            error: error instanceof Error ? error : new Error(msg),
            context: {
              error,
            },
          });
          setVimInitializationError(new Error(msg));
        }
      })();
    }
  }, [vimOS, telemetry.trackError]);

  useEffect(() => {
    if (idToken) {
      (async () => {
        try {
          const response = await fetch(`${ehrHooksUrl}/vim/zus-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
          });
          if (!response.ok) {
            const err = new Error(`Failed to fetch zus token in Vim context: ${response.status}`);
            telemetry.trackError({
              message: 'Failed to fetch zus token',
              error: err,
              context: { response: await response.text() },
            });
            setVimInitializationError(err);
            return;
          }
          const responseData = await response.json();
          setZusToken(responseData.accessToken);
          setZusBuilderId(responseData.builderId);
        } catch (error) {
          const msg = 'Failed to exchange ID token for Zus token';
          telemetry.trackError({
            message: msg,
            error: error instanceof Error ? error : new Error(msg),
            context: {
              error,
            },
          });
          setVimInitializationError(new Error(msg));
        }
      })();
    }
  }, [idToken, ehrHooksUrl, telemetry.trackError]);

  if (vimInitializationError) {
    return (
      <PageError
        title="Unable to load Zus Aggregated Profile"
        message="There was an issue loading the ZAP. Please refresh the page, and if the problem persists, contact support."
        supportEmailSubject="Vim ZAP Load Error"
        supportEmailBody={`I encountered the following error while trying to launch the ZAP in Vim:\n\n${vimInitializationError.message}\n\nPlease assist.`}
      />
    );
  }

  if (!(zusToken && patient && vimOS && idToken)) {
    let message = 'Loading application...';
    if (!vimOS) {
      message = 'Loading Vim...';
    } else if (!patient) {
      message = 'Getting Vim patient...';
    } else if (!idToken) {
      message = 'Getting ID token...';
    } else if (!zusToken) {
      message = 'Getting Zus token...';
    }
    return <LoadingSpinner message={message} centered={true} />;
  }

  return (
    <CTWProvider
      service="zap"
      serviceVersion="v1"
      ehr="vim"
      ehrContext={{
        vimEhrType: vimOS?.sessionContext?.ehrType || 'unknown',
      }}
      authToken={zusToken}
      builderId={zusBuilderId}
      devProps={{
        fallbackEnv: 'sandbox',
      }}
    >
      <VimPatientInjector
        vimOS={vimOS}
        vimPatient={patient}
        VimPatientIdentifierSystemUrl={VimPatientIdentifierSystemUrl}
      >
        <PatientProvider
          systemURL={VimPatientIdentifierSystemUrl}
          patientID={patient.identifiers?.vimPatientId}
        >
          <ZusAggregatedProfile />
        </PatientProvider>
      </VimPatientInjector>
    </CTWProvider>
  );
};
